import type { PricingCampaignsScope } from '@/entities/pricing-campaign'
import type { Frequency, TimelineRow } from '@/shared/components/timeline-grid'
import type { TFunction } from 'i18next'
import type { PricingCampaignTableRow } from '../providers/PricingCampaignsProvider'

import React, { useCallback, useEffect, useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import {
	CopyIcon,
	EllipsisVerticalIcon,
	LayersIcon,
	Link2Icon,
	PlusIcon,
	PuzzleIcon,
	Trash2Icon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApplyPricingCampaignTemplate } from '@/features/pricing-campaign/apply-template'
import { CopyPricingCampaignSettings } from '@/features/pricing-campaign/copy-settings'
import { CopyPricingCampaign } from '@/features/pricing-campaign/copy-to-og'
import { DeletePricingCampaign } from '@/features/pricing-campaign/delete'
import { DuplicatePricingCampaign } from '@/features/pricing-campaign/duplicate'
import { ReassignPricingCampaign } from '@/features/pricing-campaign/reassign-to-og'
import { SavePricingCampaignAsTemplate } from '@/features/pricing-campaign/save-as-template'

import {
	EngineBadge,
	getPricingCampaignEngine,
	GlobalPcBadge,
	globalPricingCampaignProductsLink,
	isGlobalPricingCampaign,
	loclaPricingCampaignProductsLink,
} from '@/entities/pricing-campaign'

import { TimelineGrid } from '@/shared/components/timeline-grid'
import { Button } from '@/shared/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { LinkButton } from '@/shared/components/ui/link-button'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import { intlFormat } from '@/shared/tools/dates'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

type TimelineViewProps = {
	innerRef: React.MutableRefObject<unknown>
	frequency: Frequency
	data: PricingCampaignTableRow[]
	scope?: PricingCampaignsScope
}
export const PricingCampaignsTimeline = ({
	frequency,
	data,
	scope,
	innerRef,
}: TimelineViewProps) => {
	useEffect(() => {
		ampli.pcsTimelineView()
	}, [])
	const RowContent = useCallback(
		(props: { row: TimelineRow<PricingCampaignTableRow> }) => (
			<RowContentBase {...props} />
		),
		[]
	)
	const PaneRowContent = useCallback(
		(props: { row: TimelineRow<PricingCampaignTableRow> }) => (
			<PaneRowContentBase {...props} />
		),
		[]
	)
	const rows: TimelineRow<PricingCampaignTableRow>[] = useMemo(
		() =>
			data.map((row) => ({
				id: row.campaign.id,
				start: row.campaign.start_date
					? new Date(row.campaign.start_date + 'Z')
					: null,
				end: row.campaign.end_date
					? new Date(row.campaign.end_date + 'Z')
					: null,
				data: row,
			})),
		[data]
	)

	return (
		<TimelineGrid<PricingCampaignTableRow>
			innerRef={innerRef}
			PaneRowContent={
				PaneRowContent as <T = PricingCampaignTableRow>({
					row,
				}: {
					row: TimelineRow<T>
				}) => React.ReactNode
			}
			RowContent={
				RowContent as <T = PricingCampaignTableRow>({
					row,
				}: {
					row: TimelineRow<T>
				}) => React.ReactNode
			}
			frequency={frequency}
			rows={rows}
		/>
	)
}

function formatPeriod(
	{
		start,
		end,
	}: {
		start?: string | null
		end?: string | null
	},
	t: TFunction
) {
	if (start && end) {
		return `${intlFormat(new Date(start + 'Z'))} - ${intlFormat(new Date(end + 'Z'))}`
	}
	if (start && !end) {
		return `${t('app.start_date', { defaultValue: 'Start date' })} ${intlFormat(new Date(start + 'Z'))}`
	}
	if (!start && end) {
		return `${t('app.end_date', { defaultValue: 'End date' })} ${intlFormat(new Date(end + 'Z'))}`
	}

	return null
}

function RowContentBase({
	row,
}: {
	row: TimelineRow<PricingCampaignTableRow>
}) {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const campaign = row.data!.campaign
	const engine = getPricingCampaignEngine(campaign)

	return (
		<div className='sticky left-96 z-10 space-y-1 truncate rounded-lg p-3'>
			<div className='flex space-x-1'>
				<EngineBadge size='sm' engine={engine} className='mr-1'>
					{t(engine, { defaultValue: engine })}
				</EngineBadge>
				{isGlobalPricingCampaign(campaign) && <GlobalPcBadge />}
			</div>
			<div className='truncate font-semibold'>
				<LinkButton
					className='block cursor-pointer truncate text-foreground'
					onClick={() => {
						ampli.pcsTimelineCampaignNameClick({
							destination: 'right_pane',
						})
						showModal('PRICING_CAMPAIGN_MODAL', {
							pricingCampaignId: campaign.id,
						})
					}}
				>
					{campaign.name}
				</LinkButton>
			</div>
		</div>
	)
}
function PaneRowContentBase({
	row,
}: {
	row: TimelineRow<PricingCampaignTableRow>
}) {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const campaign = row.data!.campaign
	const engine = getPricingCampaignEngine(campaign)
	const isGlobal = isGlobalPricingCampaign(campaign)
	const pricingCampaignId = campaign.id

	return (
		<div className='flex w-full justify-between'>
			<div className='w-full space-y-1 truncate'>
				<div className='flex items-center'>
					<div className='flex space-x-1'>
						<EngineBadge size='sm' engine={engine} className='mr-1'>
							{t(engine, { defaultValue: engine })}
						</EngineBadge>
						{isGlobalPricingCampaign(campaign) && <GlobalPcBadge />}
					</div>
					<div className='ml-auto text-xs text-muted-foreground'>
						{formatPeriod(
							{
								start: campaign.start_date,
								end: campaign.end_date,
							},
							t
						)}
					</div>
				</div>
				<div className='flex font-semibold'>
					<LinkButton
						className='cursor-pointer truncate text-foreground'
						onClick={() => {
							ampli.pcsTimelineCampaignNameClick({
								destination: 'left_pane',
							})
							showModal('PRICING_CAMPAIGN_MODAL', {
								pricingCampaignId: campaign.id,
							})
						}}
					>
						{campaign.name}
					</LinkButton>
					<div className='ml-auto flex items-center gap-2 text-sm'>
						<Link
							className='block cursor-pointer truncate text-xs'
							onClick={() =>
								ampli.pcsTimelineProductsCountClick({
									destination: 'left_pane',
								})
							}
							to={
								campaign.optimization_group_id
									? loclaPricingCampaignProductsLink(
											campaign,
											campaign.optimization_group_id
												? {
														optimization_group_id:
															campaign.optimization_group_id!,
													}
												: undefined
										)
									: globalPricingCampaignProductsLink(campaign)
							}
						>
							{formatNumber(row.data!.products_count)} (
							{formatNumber(
								row.data?.assortment_share || 0,
								NumberFormats.Percent
							)}
							)
						</Link>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant='ghost' size='icon-sm'>
									<EllipsisVerticalIcon />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent>
								<ApplyPricingCampaignTemplate
									pricingCampaignId={pricingCampaignId}
								>
									<DropdownMenuItem
										onClick={() => ampli.pcApplyTemplateClicked()}
									>
										<PuzzleIcon /> {t('pc.action.apply_template')}
									</DropdownMenuItem>
								</ApplyPricingCampaignTemplate>
								<SavePricingCampaignAsTemplate
									pricingCampaignId={pricingCampaignId}
								>
									<DropdownMenuItem
										onClick={() => ampli.pcSaveAsTemplateClicked()}
									>
										<PlusIcon />
										{t('settings.action.save_as_template', {
											defaultValue: 'Save as template',
										})}
									</DropdownMenuItem>
								</SavePricingCampaignAsTemplate>
								<ReassignPricingCampaign
									pricingCampaignsIds={[pricingCampaignId]}
								>
									<DropdownMenuItem
										disabled={isGlobal}
										onClick={() => ampli.pcReassignClicked()}
									>
										<Link2Icon /> {t('pc.action.reassign')}
									</DropdownMenuItem>
								</ReassignPricingCampaign>
								<CopyPricingCampaignSettings
									pricingCampaignId={pricingCampaignId}
								>
									<DropdownMenuItem
										onClick={() => ampli.pcCopySettingsClicked()}
									>
										<CopyIcon />
										{t('pc.action.copy_settings', {
											defaultValue: 'Copy settings to ...',
										})}
									</DropdownMenuItem>
								</CopyPricingCampaignSettings>
								<CopyPricingCampaign pricingCampaignsIds={[pricingCampaignId]}>
									<DropdownMenuItem
										disabled={isGlobal}
										onClick={() => ampli.pcCopyClicked()}
									>
										<CopyIcon />{' '}
										{t('pc.action.copy', { defaultValue: 'Copy to ...' })}
									</DropdownMenuItem>
								</CopyPricingCampaign>
								<DuplicatePricingCampaign
									pricingCampaignId={pricingCampaignId}
									destinationIds={[campaign.optimization_group_id ?? null]}
									destinationType='optimization_group'
								>
									<DropdownMenuItem onClick={() => ampli.pcDuplicateClicked()}>
										<LayersIcon /> {t('general_duplicate')}
									</DropdownMenuItem>
								</DuplicatePricingCampaign>
								<DeletePricingCampaign pricingCampaignId={pricingCampaignId}>
									<DropdownMenuItem
										variant='destructive'
										onClick={() => ampli.pcDeleteClicked()}
									>
										<Trash2Icon /> {t('general_delete')}
									</DropdownMenuItem>
								</DeletePricingCampaign>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</div>
			</div>
		</div>
	)
}
